import type { ReactNode } from "react";
import { useCallback, useEffect, useReducer, useRef } from "react";
import { useTranslation } from "react-i18next";

import { earlyAccessSchema, validateFormFields, zodErrorToI18n } from "@/lib/early-access-schema";

import { FormView } from "./FormView";
import { loadTurnstileScript } from "./load-turnstile-script";
import { SuccessView } from "./SuccessView";
import {
	formReducer,
	getFormData,
	getTurnstileMessage,
	INITIAL_STATE,
	isSubmitDisabled,
	mapApiError,
} from "./state";

const TURNSTILE_SITEKEY = import.meta.env.VITE_TURNSTILE_SITEKEY;

interface EarlyAccessModalProps {
	open: boolean;
	onClose: () => void;
}

function ModalCloseButton({ onClose }: { onClose: () => void }): ReactNode {
	const { t } = useTranslation();

	return (
		<button className="modal-close" type="button" aria-label={t("cta.close")} onClick={onClose}>
			<svg
				width="18"
				height="18"
				viewBox="0 0 18 18"
				fill="none"
				stroke="currentColor"
				strokeWidth="1.5"
			>
				<path d="M4 4l10 10M14 4L4 14" />
			</svg>
		</button>
	);
}

export function EarlyAccessModal({ open, onClose }: EarlyAccessModalProps): ReactNode {
	const { t } = useTranslation();
	const [state, dispatch] = useReducer(formReducer, INITIAL_STATE);
	const dialogRef = useRef<HTMLDialogElement>(null);
	const formRef = useRef<HTMLFormElement>(null);
	const nameRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		const dialog = dialogRef.current;
		if (!dialog) return;

		if (open) {
			if (!dialog.open) dialog.showModal();
			if (nameRef.current) nameRef.current.focus();
			document.body.style.overflow = "hidden";
		} else {
			if (dialog.open) dialog.close();
			document.body.style.overflow = "";
		}

		return () => {
			document.body.style.overflow = "";
			if (dialog.open) dialog.close();
		};
	}, [open]);

	useEffect(() => {
		if (!open) return;

		if (!TURNSTILE_SITEKEY) {
			dispatch({ type: "TURNSTILE_LOAD_ERROR" });
			return;
		}

		let cancelled = false;
		dispatch({ type: "TURNSTILE_LOADING" });

		const loadTimeout = window.setTimeout(() => {
			if (!cancelled) dispatch({ type: "TURNSTILE_LOAD_ERROR" });
		}, 15_000);

		loadTurnstileScript()
			.then(() => {
				if (!cancelled) dispatch({ type: "TURNSTILE_READY" });
			})
			.catch(() => {
				if (!cancelled) dispatch({ type: "TURNSTILE_LOAD_ERROR" });
			})
			.finally(() => {
				window.clearTimeout(loadTimeout);
			});

		return () => {
			cancelled = true;
			window.clearTimeout(loadTimeout);
		};
	}, [open, state.widgetKey]);

	const handleClose = useCallback((): void => {
		dispatch({ type: "RESET" });
		onClose();
	}, [onClose]);

	const handleDialogCancel = useCallback(
		(e: React.SyntheticEvent<HTMLDialogElement>): void => {
			e.preventDefault();
			handleClose();
		},
		[handleClose],
	);

	const handleToken = useCallback((token: string): void => {
		dispatch({ type: "SET_TOKEN", token });
	}, []);

	const handleTurnstileError = useCallback((): void => {
		dispatch({ type: "TURNSTILE_WIDGET_ERROR" });
	}, []);

	const checkFormValidity = useCallback((): void => {
		if (!formRef.current) return;
		const data = getFormData(formRef.current);
		dispatch({ type: "SET_VALID", valid: validateFormFields(data) });
	}, []);

	const handleSubmit = useCallback(
		async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
			e.preventDefault();

			if (!formRef.current) return;
			const data = getFormData(formRef.current);
			data.turnstileToken = state.turnstileToken;

			const parsed = earlyAccessSchema.safeParse(data);
			if (!parsed.success) {
				dispatch({
					type: "VALIDATION_ERROR",
					errors: zodErrorToI18n(parsed.error.issues, t),
				});
				return;
			}

			dispatch({ type: "SUBMITTING" });
			try {
				const res = await fetch("/api/early-access", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(parsed.data),
				});
				if (res.ok) {
					dispatch({ type: "SUCCESS" });
				} else {
					const body = (await res.json().catch(() => null)) as {
						error?: string;
					} | null;
					dispatch({
						type: "ERROR",
						msg: mapApiError(body?.error, t),
					});
				}
			} catch {
				dispatch({ type: "ERROR", msg: t("cta.errorGeneric") });
			}
		},
		[state.turnstileToken, t],
	);

	const turnstileMessage = getTurnstileMessage(state, TURNSTILE_SITEKEY, t);

	return (
		<dialog
			ref={dialogRef}
			className="modal-dialog"
			tabIndex={-1}
			aria-label={t("cta.modalTitle")}
			onCancel={handleDialogCancel}
		>
			<div
				className="modal-backdrop"
				role="presentation"
				aria-hidden="true"
				onClick={handleClose}
			/>
			<div className="modal">
				<ModalCloseButton onClose={handleClose} />
				{state.status === "success" ? (
					<SuccessView onClose={handleClose} />
				) : (
					<FormView
						formRef={formRef}
						nameRef={nameRef}
						state={state}
						open={open}
						sitekey={TURNSTILE_SITEKEY}
						turnstileMessage={turnstileMessage}
						submitDisabled={isSubmitDisabled(state)}
						onSubmit={handleSubmit}
						onFieldChange={checkFormValidity}
						onToken={handleToken}
						onTurnstileError={handleTurnstileError}
					/>
				)}
			</div>
		</dialog>
	);
}
